import { defineMessages } from 'react-intl';

import { Providers } from '../../enums/providers';
import { getUserFriendlyMessagesForErrorResponse } from '../../errors/user-friendly-error-message-service';
import { FeatureFlags } from '../../feature-flags/feature-flags';
import { HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING } from '../../wallet/utilities/constants';
import Global from '../global-selectors';
import { determineShouldShowCostReviewByError } from '../quickship/quickship.helpers';

const messages = defineMessages({
  shipsuranceFundErrorMessage: {
    description: 'shipsurance.shipsuranceFundErrorMessageTitle',
    defaultMessage: 'Insufficient Shipsurance Funds',
    id: 'kMrp9m',
  },
  shipsurancePurchaseFailure: {
    description: 'shipsurance.failedToPurchaseShipsurance',
    defaultMessage: 'Failed to purchase Shipsurance',
    id: 'gFf2Fo',
  },
  walletFundErrorMessage: {
    description: 'Error message describing there is not enough funds/money in the users wallet to make the purchase',
    defaultMessage: 'Insufficient Wallet Funds',
    id: 'btv8Ix',
  },
  upsTermsNotAccepted: {
    description: 'Error message when UPS label terms have not been accepted',
    defaultMessage: 'UPS Label Terms Not Accepted',
    id: 'PPzySm',
  },
  upsInvoicedNotification: {
    description: 'Error message when UPS Invoiced label purchase fails due to missing Shipment Invoice payment method',
    defaultMessage: 'UPS Invoiced Notification',
    id: 'mGI2GN',
  },
});

/**
 * Helper to check if UPS Wallet terms need to be accepted.
 * Used for standalone flows (returns, scan-to-print) that don't go through cost review.
 * @param {object} args
 * @param {string} args.fulfillmentPlanId - The fulfillment plan ID
 * @param {() => object} args.getState - Redux getState function
 * @returns {boolean} - True if UPS terms need to be accepted
 */
export const shouldShowCostReviewForUpsTerms = ({ fulfillmentPlanId, getState }) => {
  const state = getState();
  const { enableUpsTerms2 } = new FeatureFlags({ state });

  if (!enableUpsTerms2) {
    return false;
  }

  const sellerLabelProviderId = Global.selectEntitiesFulfillmentPlanSellerLabelProviderID(state, { fulfillmentPlanId });

  if (!sellerLabelProviderId) {
    return false;
  }

  const sellerLabelProviders = Global.selectSellerLabelProviders(state);
  const sellerLabelProvider = sellerLabelProviders?.find((slp) => slp.sellerLabelProviderId === sellerLabelProviderId);

  if (!sellerLabelProvider) {
    return false;
  }

  const isUpsWallet = sellerLabelProvider.labelProviderId === Providers.UPSWallet.labelProviderId;

  if (!isUpsWallet) {
    return false;
  }

  const hasAcceptedUpsTermsSetting = Global.selectSellerSellerSetting(state, { settingName: HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING });

  return hasAcceptedUpsTermsSetting !== 'true';
};

export const shouldShowSingleLabelCostReview = ({ responseData, quickshipProps, hasAlreadyViewedCostReview = false }) => {
  const { errors, stringErrors } = getUserFriendlyMessagesForErrorResponse(responseData);
  const fundingErrorMessage = stringErrors.find((error) => {
    return (
      error === messages.shipsuranceFundErrorMessage.defaultMessage ||
      error === messages.shipsurancePurchaseFailure.defaultMessage ||
      error === messages.walletFundErrorMessage.defaultMessage ||
      error === messages.upsTermsNotAccepted.defaultMessage ||
      error === messages.upsInvoicedNotification.defaultMessage
    );
  });

  // mapping errors to match that of the handleLabelPurchaseFailure errorResponse
  const mappedErrorCodeList = errors.map((err) => ({
    errorCode: err.apiError?.Code,
  }));

  const hasCostReviewError = fundingErrorMessage && determineShouldShowCostReviewByError(mappedErrorCodeList, quickshipProps);

  return hasCostReviewError && !hasAlreadyViewedCostReview;
};
